import { yupResolver } from '@hookform/resolvers/yup';
import {
  Input, message, Modal, Typography
} from 'antd';
import React, { useRef } from 'react';
import ReCAPTCHA from 'react-google-recaptcha';
import { Controller, useForm } from 'react-hook-form';
import { useMutation } from 'react-query';

import Button from 'components/atoms/Button';
import TextAreaCustom from 'components/atoms/TextArea';
import { requestDemoService } from 'services/systems';
import { requestDemoSchema } from 'utils/schemas';

interface IRequestDemo {
  fullName: string;
  companyName: string;
  email: string;
  phone: string;
  role: string;
  note: string;
}

interface ModalRegisterProps {
  isOpen: boolean;
  onClose: () => void;
}

const ModalRegister: React.FC<ModalRegisterProps> = ({ isOpen, onClose }) => {
  const {
    control,
    formState: { errors, isValid },
    reset,
    setError,
    trigger,
    handleSubmit,
  } = useForm<IRequestDemo>({
    defaultValues: {
      fullName: '',
      companyName: '',
      email: '',
      phone: '',
      role: '',
      note: '',
    },
    resolver: yupResolver(requestDemoSchema),
    mode: 'onChange',
  });

  const [messageApi, contextHolder] = message.useMessage();

  const recaptchaRef = useRef<ReCAPTCHA | null>(null);

  const success = (contentString: string) => {
    messageApi.open({
      type: 'success',
      content: contentString,
      className: 'p-home_message-success',
    });
  };

  const { mutate: requestDemo } = useMutation({
    mutationKey: ['requestDemo'],
    mutationFn: requestDemoService,
    onSuccess: () => {
      success('Request demo successfully');
    },
    onError: (error) => {
      if (Array.isArray(error)) {
        error.forEach((err) => {
          setError(err.field, {
            message: err.message,
          });
        });
      } else {
        message.error('Something went wrong, please try again later');
      }
    },
  });

  const onSubmit = async (data: IRequestDemo) => {
    if (!recaptchaRef.current) return;
    const valid = await trigger();
    if (!valid) return;

    try {
      const token = await recaptchaRef.current.executeAsync();
      if (!token) return;
      requestDemo({
        ...{
          ...data,
          name: data.fullName,
        },
        ggRecaptchaToken: token
      });
    } catch (error) {
      message.error('Something went wrong, please try again later');
    } finally {
      recaptchaRef.current.reset();
      reset();
      onClose();
    }
  };

  // const hasErrors = !!errors.name || !!errors.phone || !!errors.email
  //   || !!errors.role || !!errors.companyName || !!errors.note;

  return (
    <Modal
      open={isOpen}
      onCancel={onClose}
      footer={(
        <Button
          disabled={!isValid}
          onClick={handleSubmit(onSubmit)}
          className="p-home_demo_submit fButton"
        >
          Send
        </Button>
      )}
      centered
      width={915}
      className="p-home_modal"
      closable
      title={(
        <div className="">
          <Typography.Title level={5} className="p-home_formHeading">
            Contact our sales team
          </Typography.Title>
          <Typography.Text style={{ fontWeight: 400 }}>
            Share your details so we can schedule a personalized demo for you.
          </Typography.Text>
        </div>
      )}
    >
      <div className="p-home_demo">
        <form onSubmit={handleSubmit(onSubmit)} className="p-home_demo_formContain">
          <div className="p-home_demo_formLayout">
            <div className="p-home_demo_formGroup">
              <Typography.Text className="p-home_demo_formLabel" strong>
                Full name
                {' '}
                <span className="p-home_demo_quotes">*</span>
              </Typography.Text>
              <Controller
                name="fullName"
                control={control}
                render={({ field }) => (
                  <>
                    <Input
                      size="large"
                      {...field}
                      className="p-home_demo_formInput"
                      placeholder="Enter your name
"
                    />
                    {errors.fullName && (
                      <Typography.Text className="p-home_demo_error">{errors.fullName.message}</Typography.Text>
                    )}
                  </>
                )}
              />
            </div>
            <div className="p-home_demo_formGroup">
              <Typography.Text className="p-home_demo_formLabel" strong>
                Phone number
                {' '}
                <span className="p-home_demo_quotes">*</span>
              </Typography.Text>
              <Controller
                name="phone"
                control={control}
                render={({ field }) => (
                  <>
                    <Input
                      size="large"
                      {...field}
                      onKeyPress={(e: React.KeyboardEvent<HTMLInputElement>) => {
                        const { value } = e.currentTarget;

                        if (/[0-9]/.test(e.key)) return;

                        if (e.key === '+' && value.length === 0 && !value.includes('+')) {
                          return;
                        }

                        e.preventDefault();
                      }}
                      onPaste={(e) => {
                        const pasted = e.clipboardData.getData('text');
                        if (!/^[0-9+]+$/.test(pasted.replace(/^\+84/, '0'))) {
                          e.preventDefault();
                        }
                      }}
                      inputMode="numeric"
                      className="p-home_demo_formInput"
                      placeholder="Enter your number"
                    />
                    {errors.phone && (
                      <Typography.Text className="p-home_demo_error">{errors.phone.message}</Typography.Text>
                    )}
                  </>
                )}
              />
            </div>
          </div>
          <div className="p-home_demo_formLayout">
            <div className="p-home_demo_formGroupRow">
              <Typography.Text className="p-home_demo_formLabel" strong>
                Email
                {' '}
                <span className="p-home_demo_quotes">*</span>
              </Typography.Text>
              <Controller
                name="email"
                control={control}
                render={({ field }) => (
                  <>
                    <Input
                      size="large"
                      {...field}
                      placeholder="Enter your email"
                    />
                    {errors.email && (
                      <Typography.Text className="p-home_demo_error">
                        {errors.email.message}
                      </Typography.Text>
                    )}
                  </>
                )}
              />
            </div>
          </div>
          <div className="p-home_demo_formLayout">
            <div className="p-home_demo_formGroup">
              <Typography.Text className="p-home_demo_formLabel" strong>
                Role
              </Typography.Text>
              <Controller
                name="role"
                control={control}
                render={({ field }) => (
                  <>
                    <Input size="large" {...field} className="p-home_demo_formInput" placeholder="Enter your role" />
                    {errors.role && (
                      <Typography.Text className="p-home_demo_error">{errors.role.message}</Typography.Text>
                    )}
                  </>
                )}
              />
            </div>
            <div className="p-home_demo_formGroup">
              <Typography.Text className="p-home_demo_formLabel" strong>
                Company Name
              </Typography.Text>
              <Controller
                name="companyName"
                control={control}
                render={({ field }) => (
                  <Input size="large" {...field} className="p-home_demo_formInput" placeholder="Enter company name" />
                )}
              />
            </div>
          </div>
          <div className="p-home_demo_formGroupText">
            <Typography.Text className="p-home_demo_formLabel p-home_demo_mt6" strong>
              Message
            </Typography.Text>
            <Controller
              name="note"
              control={control}
              render={({ field }) => (
                <>
                  <TextAreaCustom
                    showCount={{
                      formatter: ({ count }) => `${count}/${500}`,
                    }}
                    {...field}
                    className="p-home_demo_formInput p-home_demo_textarea"
                    placeholder="Enter message"
                    rows={6}
                  />
                  {errors.note && (
                    <Typography.Text className="p-home_demo_error">{errors.note.message}</Typography.Text>
                  )}
                </>
              )}
            />
          </div>
        </form>
      </div>
      <ReCAPTCHA ref={recaptchaRef} sitekey="6Lcp4MIZAAAAABHdGwULZhEv01tu2iODEDzSterA" size="invisible" />
      {contextHolder}
    </Modal>
  );
};

export default ModalRegister;
